import { Box, Button, CircularProgress, Divider, Link as MuiLink, Stack, Tooltip, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import { Link as RouterLink } from "react-router-dom";
import { useGetMany } from "react-admin";
import { SectionCard } from "@/components/common/SectionCard";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { getCarrierLabel } from "@/components/common/PolicyField";
import { DocumentRead, ResourceName } from "@/types";
import { UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { policyDisplayLabel, useStoplossClaimContext } from "../../hooks/useStoplossClaimContext";
import { ClaimNotesCard } from "../details/ClaimNotesCard";
import { StoplossClaimHoldBanner } from "../StoplossClaimHoldBanner";
import { LinkedClause, otherClaimsParts, overDeductibleParts } from "./claimAmountCopy";
import { TrackView, useTrackActions } from "./useTrackActions";
import { DeductiblePeek } from "./DeductiblePeek";
import { OtherClaimsPeek } from "./OtherClaimsPeek";
import { useState } from "react";

// Rail type scale, per the v6 mockup: labels 12.5px/600 muted, amount 20px/700,
// captions 12px, small buttons. Kept here so the rail and its collapsed bar agree.
const LABEL_SX = { fontSize: "0.875rem", fontWeight: 600, color: "text.secondary" } as const;
const CAPTION_SX = { fontSize: "0.8125rem", color: "text.secondary" } as const;
const BUTTON_SX = { textTransform: "none", fontSize: "0.875rem", fontWeight: 600, height: 36 } as const;

/** The word in a caption that opens a peek: a link-styled button, since it acts rather than navigates. */
const PeekLink = ({
  onClick,
  children,
}: {
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
}) => (
  <MuiLink
    component="button"
    type="button"
    onClick={onClick}
    underline="hover"
    sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
  >
    {children}
  </MuiLink>
);

const TrackRow = ({
  label,
  tooltip,
  dotColor,
  view,
  icon,
  variant,
  hideAction = false,
  children,
}: {
  label: string;
  tooltip?: string;
  dotColor: string;
  view: TrackView;
  icon: React.ReactNode;
  variant: "contained" | "outlined";
  /** While the claim is held the hold strip stands in for every action button. */
  hideAction?: boolean;
  children?: React.ReactNode;
}) => (
  <Box data-testid={`track-${label.toLowerCase().replace(/\W+/g, "-")}`}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Tooltip title={tooltip ?? ""} placement="top-start">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0 }} />
          <Typography sx={LABEL_SX} noWrap>
            {label}
          </Typography>
        </Box>
      </Tooltip>
      {view.chip ? <StatusChip label={view.chip.label} color={view.chip.color} /> : null}
    </Box>
    {children}
    {view.actionLabel && !hideAction ? (
      <Tooltip title={view.disabled && view.reason ? view.reason : ""} placement="top">
        <span style={{ display: "block" }}>
          <Button
            fullWidth
            size="small"
            variant={variant}
            color={variant === "contained" ? "primary" : "inherit"}
            disabled={view.disabled || view.pending}
            onClick={view.onAction}
            startIcon={view.pending ? <CircularProgress size={14} color="inherit" /> : icon}
            sx={{ mt: 1, ...BUTTON_SX }}
            disableElevation
          >
            {view.actionLabel}
          </Button>
        </span>
      </Tooltip>
    ) : null}
    {view.disabled && view.reason && view.actionLabel && !hideAction ? (
      <Typography sx={{ ...CAPTION_SX, mt: 0.5 }} display="block">
        {view.reason}
      </Typography>
    ) : null}
  </Box>
);

/** The fields the Aura sender needs, with the gaps marked and a fix link (#7582 AC3). */
const AuraReadinessList = ({ missing, onRepair }: { missing: string[]; onRepair?: () => void }) => {
  const { claim, claimant, client, policy } = useStoplossClaimContext();
  const rows: { label: string; value: string; fixable: boolean }[] = [
    { label: "Client", value: client?.client_legal_name ?? "", fixable: true },
    { label: "Policy", value: policy ? policyDisplayLabel(policy) : "", fixable: true },
    { label: "Claimant", value: claimant?.claimant_name ?? "", fixable: true },
    {
      label: "Stop-loss amount",
      value: (claim.total_paid_amount ?? null) !== null ? formatUSD(claim.total_paid_amount as number) : "",
      fixable: false,
    },
  ];
  return (
    <Box data-testid="aura-readiness" sx={{ mt: 1 }}>
      <Typography sx={CAPTION_SX}>
        Aura funding needs four things on the claim. {missing.length === 1 ? "One is" : `${missing.length} are`}{" "}
        missing.
      </Typography>
      <Stack divider={<Divider />} sx={{ mt: 0.5 }}>
        {rows.map((r) => {
          const isMissing = missing.includes(r.label);
          return (
            <Box key={r.label} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.5 }}>
              <Typography sx={{ fontSize: "0.75rem" }}>{r.label}</Typography>
              {isMissing ? (
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }} color="error.main">
                  Missing
                  {r.fixable && onRepair ? (
                    <>
                      {" "}
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={onRepair}
                        sx={{ fontSize: "inherit", verticalAlign: "baseline" }}
                      >
                        Fix
                      </MuiLink>
                    </>
                  ) : (
                    <Tooltip title="Written by the engine from the medical and Rx claims on this stop-loss claim; it fills in when they load.">
                      <span> (engine-written)</span>
                    </Tooltip>
                  )}
                </Typography>
              ) : (
                <Typography sx={CAPTION_SX} noWrap maxWidth={160}>
                  {r.value || "—"}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

/**
 * The claim's documents, listed in the rail (#7585). This is their one home on
 * the page; a row opens the preview drawer. Attachments and upload wait on a
 * stop-loss attachments relation on the API.
 */
const RailDocumentsCard = ({ onOpenDocument }: { onOpenDocument: (doc: DocumentRead) => void }) => {
  const { claim } = useStoplossClaimContext();
  const ids = (claim.source_documents ?? []).map(String);
  const { data: docs = [] } = useGetMany<DocumentRead>(ResourceName.DOCUMENTS, { ids }, { enabled: ids.length > 0 });
  return (
    <SectionCard compact title="Documents" badge={ids.length ? ids.length : undefined}>
      {docs.length === 0 ? (
        <Typography sx={CAPTION_SX}>No documents on this claim.</Typography>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {docs.map((d) => (
            <Box key={String(d.id)} sx={{ py: 0.75, display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => onOpenDocument(d)}
                  underline="hover"
                  sx={{ textAlign: "left", fontSize: "0.8125rem", color: "text.primary", display: "block" }}
                >
                  {composeDocumentLabel(d)}
                </MuiLink>
                <Typography sx={CAPTION_SX}>{d.type ? formatToTitleCase(String(d.type)) : "Document"}</Typography>
              </Box>
              <MuiLink
                component={RouterLink}
                to={`/documents/${d.id}/show`}
                underline="hover"
                sx={{ fontSize: "0.75rem", whiteSpace: "nowrap", alignSelf: "center" }}
              >
                Open page
              </MuiLink>
            </Box>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
};

interface StoplossClaimRailProps {
  onOpenDocument: (doc: DocumentRead) => void;
  onRepairLinks?: () => void;
}

/**
 * The action rail (#7582): claim amount with its derivation, the two tracks,
 * reimbursed-so-far; then notes and the documents card.
 */
export const StoplossClaimRail = ({ onOpenDocument, onRepairLinks }: StoplossClaimRailProps) => {
  const ctx = useStoplossClaimContext();
  const {
    claim,
    effectiveDeductible,
    isASD,
    isFamily,
    isFrozen,
    reconciles,
    reviewPending,
    spendOverDeductible,
    reimbursedTotal,
    policyAccelEnabled,
    priorShown,
    otherClaimsCount,
  } = ctx;
  const { aura, carrier, missingAuraFields, confirmDialog } = useTrackActions();
  const [deductibleAnchor, setDeductibleAnchor] = useState<HTMLElement | null>(null);
  const [othersAnchor, setOthersAnchor] = useState<HTMLElement | null>(null);

  const claimAmount = claim.total_paid_amount ?? 0;
  const outstanding = Math.max(claimAmount - reimbursedTotal, 0);
  // The deductible clause describes the individual specific deductible, so it is
  // skipped for ASD and family claims. When the engine's rule does not visibly
  // reproduce the amount, the clause is marked as today's position.
  const overParts =
    isASD || isFamily
      ? null
      : overDeductibleParts(spendOverDeductible, effectiveDeductible?.amount, effectiveDeductible?.source);
  // When the engine's rule does not visibly reproduce the amount, the clause is marked as today's position.
  const over: LinkedClause | null =
    overParts && !reconciles
      ? { ...overParts, before: `Today ${overParts.before.charAt(0).toLowerCase()}${overParts.before.slice(1)}` }
      : overParts;
  const others = otherClaimsParts(priorShown, otherClaimsCount, isFrozen);
  const showReadiness = (policyAccelEnabled || !ctx.policy) && !claim.aura_submitted_at && missingAuraFields.length > 0;
  const carrierName = ctx.policy ? getCarrierLabel(ctx.policy) : undefined;

  return (
    <>
      <SectionCard compact noDivider>
        <Box data-testid="rail-claim-amount">
          {/* Stacked: label, figure, caption. A big figure beside a small label never
              baseline-aligns cleanly, and a wide figure (an eight-digit claim) has room. */}
          <Typography sx={LABEL_SX}>Claim amount</Typography>
          <Typography
            sx={{
              fontSize: "1.75rem",
              fontWeight: 700,
              lineHeight: 1.15,
              mt: 0.25,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatUSD(claimAmount)}
          </Typography>
          {(over || others) && (
            <Typography component="div" sx={{ ...CAPTION_SX, mt: 0.5 }}>
              {over ? (
                <>
                  {over.before}
                  <PeekLink onClick={(e) => setDeductibleAnchor(e.currentTarget)}>{over.link}</PeekLink>
                  {over.after}
                </>
              ) : null}
              {over && others ? ". " : ""}
              {others ? (
                <>
                  {others.before}
                  <PeekLink onClick={(e) => setOthersAnchor(e.currentTarget)}>{others.link}</PeekLink>
                  {others.after}
                </>
              ) : null}
            </Typography>
          )}
          <DeductiblePeek anchorEl={deductibleAnchor} onClose={() => setDeductibleAnchor(null)} />
          <OtherClaimsPeek anchorEl={othersAnchor} onClose={() => setOthersAnchor(null)} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <TrackRow
            label="Aura funding"
            dotColor="primary.main"
            view={aura}
            icon={<CloudUploadIcon fontSize="small" />}
            variant="contained"
            hideAction={reviewPending}
          >
            {showReadiness ? <AuraReadinessList missing={missingAuraFields} onRepair={onRepairLinks} /> : null}
          </TrackRow>
          <TrackRow
            label="Carrier"
            tooltip={carrierName}
            dotColor="secondary.main"
            view={carrier}
            icon={<SendIcon fontSize="small" />}
            variant="outlined"
            hideAction={reviewPending}
          />
          {/* The blocker takes the place of the actions it blocks. */}
          {reviewPending ? <StoplossClaimHoldBanner compact /> : null}
        </Stack>

        <Typography component="div" sx={{ ...CAPTION_SX, mt: 1.5 }} data-testid="rail-reimbursed">
          Reimbursed so far {formatUSD(reimbursedTotal)}, outstanding {formatUSD(outstanding)}
        </Typography>
      </SectionCard>

      <StoplossClaimRailCards onOpenDocument={onOpenDocument} />
      {confirmDialog}
    </>
  );
};

/**
 * Notes and documents: the rail's lower cards. Rendered in the rail at ≥700 px
 * and under the tabs when the rail is collapsed, so neither leaves the page at
 * any width (review on !5015).
 */
export const StoplossClaimRailCards = ({ onOpenDocument }: { onOpenDocument: (doc: DocumentRead) => void }) => {
  const { claim } = useStoplossClaimContext();
  return (
    <>
      <ClaimNotesCard claim={claim} />
      <RailDocumentsCard onOpenDocument={onOpenDocument} />
    </>
  );
};

/**
 * The rail's stand-in below 700 px (#7581 AC2): amount left, the two track
 * buttons right, then the reimbursed line and, when funding is blocked on a
 * missing field, the readiness list, so the narrow page says why.
 */
export const StoplossClaimActionBar = ({ onRepairLinks }: { onRepairLinks?: () => void }) => {
  const ctx = useStoplossClaimContext();
  const { claim, reimbursedTotal, policyAccelEnabled } = ctx;
  const { aura, carrier, confirmDialog, missingAuraFields } = useTrackActions();
  const claimAmount = claim.total_paid_amount ?? 0;
  const outstanding = Math.max(claimAmount - reimbursedTotal, 0);
  const showReadiness = (policyAccelEnabled || !ctx.policy) && !claim.aura_submitted_at && missingAuraFields.length > 0;
  const renderButton = (view: TrackView, variant: "contained" | "outlined", icon: React.ReactNode) =>
    view.actionLabel ? (
      <Tooltip title={view.disabled && view.reason ? view.reason : ""}>
        <span>
          <Button
            size="small"
            variant={variant}
            color={variant === "contained" ? "primary" : "inherit"}
            disabled={view.disabled || view.pending}
            onClick={view.onAction}
            startIcon={view.pending ? <CircularProgress size={14} color="inherit" /> : icon}
            sx={{ ...BUTTON_SX, whiteSpace: "nowrap" }}
            disableElevation
          >
            {view.actionLabel}
          </Button>
        </span>
      </Tooltip>
    ) : view.chip ? (
      <StatusChip label={view.chip.label} color={view.chip.color} size={UIComponentSize.Small} />
    ) : null;

  return (
    <>
      <SectionCard compact noDivider>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 140, lineHeight: 1.1 }}>
            <Typography sx={CAPTION_SX} display="block">
              Claim amount
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>{formatUSD(claimAmount)}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {renderButton(aura, "contained", <CloudUploadIcon fontSize="small" />)}
            {renderButton(carrier, "outlined", <SendIcon fontSize="small" />)}
          </Box>
        </Box>
        <Typography component="div" sx={{ ...CAPTION_SX, mt: 1 }} data-testid="bar-reimbursed">
          Reimbursed so far {formatUSD(reimbursedTotal)}, outstanding {formatUSD(outstanding)}
        </Typography>
        {showReadiness ? <AuraReadinessList missing={missingAuraFields} onRepair={onRepairLinks} /> : null}
      </SectionCard>
      {confirmDialog}
    </>
  );
};
